import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";

/**
 * Apple-style legal document layout.
 * Props:
 *  - title: string
 *  - lastUpdated: string  (Spanish long date; default today)
 *  - toc: [{id, label}]
 *  - children: rendered body (sections must have matching ids for TOC)
 */
export default function LegalPage({ title, lastUpdated, toc = [], children, testid }) {
  const contentRef = useRef(null);
  const today = useMemo(() => new Date().toLocaleDateString("es-MX", {
    year: "numeric", month: "long", day: "numeric",
  }), []);

  useEffect(() => {
    // Smooth-scroll into sections when hash changes
    const onHash = () => {
      const id = window.location.hash.replace("#", "");
      if (!id) return;
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    onHash();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const printPdf = () => window.print();

  return (
    <div className="legal-shell" data-testid={testid || "legal-page"}>
      <header className="legal-topbar">
        <Link to="/" className="legal-back" data-testid="legal-back-link">
          <ArrowLeft size={16} /> Mi Contador
        </Link>
        <button type="button" className="legal-print" onClick={printPdf} data-testid="legal-print-button">
          <Download size={14} /> Descargar PDF
        </button>
      </header>

      <main className="legal-container">
        <div className="legal-eyebrow">LEGAL</div>
        <h1 className="legal-title" data-testid="legal-title">{title}</h1>
        <p className="legal-updated">Última actualización: <b>{lastUpdated || today}</b></p>

        {toc.length > 0 && (
          <nav className="legal-toc" aria-label="Contenido" data-testid="legal-toc">
            <b>Contenido</b>
            <ol>
              {toc.map((t, i) => (
                <li key={t.id}>
                  <a href={`#${t.id}`}>{i + 1}. {t.label}</a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <article className="legal-body" ref={contentRef}>
          {children}
        </article>

        <footer className="legal-footer-bar">
          <Link to="/legal/privacidad">Aviso de Privacidad</Link>
          <span>·</span>
          <Link to="/legal/terminos">Términos y Condiciones</Link>
          <span>·</span>
          <Link to="/legal/cookies">Política de Cookies</Link>
        </footer>
      </main>
    </div>
  );
}
